import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAllLogs,
  getCombinedStats,
  clearPwaLogs,
  getSeverityBadge,
  formatLogTimestamp,
  type LogEntry,
  type CombinedLogStats,
  type Severity,
} from '@/services/diagnostics';
import { deleteAllProjects, deleteTestUsers } from '@/services/config';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebase';

type SeverityFilter = 'all' | Severity;
type SourceFilter = 'all' | 'pwa' | 'cloud-functions';

export function DiagnosticsPage() {
  const queryClient = useQueryClient();
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [hoursBack, setHoursBack] = useState(24);

  // State for timestamp fix tool
  const [fixingTimestamps, setFixingTimestamps] = useState(false);
  const [timestampFixResult, setTimestampFixResult] = useState<any>(null);

  // State for Portal-CRM sync comparison
  const [comparingProjects, setComparingProjects] = useState(false);
  const [compareResult, setCompareResult] = useState<any>(null);
  const [importingProject, setImportingProject] = useState<string | null>(null);

  // State for deleting all projects
  const [deletingProjects, setDeletingProjects] = useState(false);
  const [deleteResult, setDeleteResult] = useState<any>(null);

  // State for deleting test users
  const [deletingUsers, setDeletingUsers] = useState(false);
  const [deleteUsersResult, setDeleteUsersResult] = useState<any>(null);

  // Fetch logs
  const {
    data: logs = [],
    isLoading: logsLoading,
    error: logsError,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ['diagnostics-logs', hoursBack],
    queryFn: () => getAllLogs({ hours: hoursBack, limit: 200 }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch stats
  const {
    data: stats,
    isLoading: statsLoading,
  } = useQuery({
    queryKey: ['diagnostics-stats'],
    queryFn: getCombinedStats,
    refetchInterval: 60000, // Refresh every minute
  });

  // Filter logs
  const filteredLogs = logs.filter(log => {
    if (severityFilter !== 'all' && log.severity !== severityFilter) {
      return false;
    }
    if (sourceFilter !== 'all' && log.source !== sourceFilter) {
      return false;
    }
    return true;
  });

  // Group logs by severity for summary
  const severityCounts = {
    critical: filteredLogs.filter(l => l.severity === 'critical').length,
    high: filteredLogs.filter(l => l.severity === 'high').length,
    medium: filteredLogs.filter(l => l.severity === 'medium').length,
    low: filteredLogs.filter(l => l.severity === 'low').length,
  };

  // Handle clear PWA logs
  const handleClearPwaLogs = useCallback(() => {
    if (confirm('Are you sure you want to clear all PWA logs?')) {
      clearPwaLogs();
      queryClient.invalidateQueries({ queryKey: ['diagnostics-logs'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostics-stats'] });
    }
  }, [queryClient]);

  // Handle fix broken timestamps
  const handleFixTimestamps = useCallback(async (dryRun: boolean) => {
    try {
      setFixingTimestamps(true);
      setTimestampFixResult(null);

      const fixBrokenTimestamps = httpsCallable(functions, 'crm_fixBrokenTimestamps');
      const result = await fixBrokenTimestamps({ dryRun });

      setTimestampFixResult(result.data);

      if (!dryRun) {
        // Refresh project list if we actually fixed anything
        queryClient.invalidateQueries({ queryKey: ['projects'] });
      }
    } catch (error: any) {
      console.error('Failed to fix timestamps:', error);
      alert(`Error: ${error.message || 'Failed to run timestamp fix'}`);
    } finally {
      setFixingTimestamps(false);
    }
  }, [queryClient]);

  // Handle compare Portal and CRM projects
  const handleCompareProjects = useCallback(async () => {
    try {
      setComparingProjects(true);
      setCompareResult(null);

      const comparePortalCRM = httpsCallable(functions, 'crm_comparePortalCRMProjects');
      const result = await comparePortalCRM({});

      setCompareResult(result.data);
    } catch (error: any) {
      console.error('Failed to compare projects:', error);
      alert(`Error: ${error.message || 'Failed to compare Portal and CRM projects'}`);
    } finally {
      setComparingProjects(false);
    }
  }, []);

  // Handle import Portal project to CRM
  const handleImportProject = useCallback(async (portalProjectId: string, referralSourceId?: string) => {
    try {
      setImportingProject(portalProjectId);

      const importProject = httpsCallable(functions, 'crm_importPortalProjectToCRM');
      const result = await importProject({ portalProjectId, referralSourceId });

      alert(`Successfully imported project: ${(result.data as any).projectCode}`);

      // Refresh comparison and project list
      handleCompareProjects();
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    } catch (error: any) {
      console.error('Failed to import project:', error);
      alert(`Error: ${error.message || 'Failed to import Portal project'}`);
    } finally {
      setImportingProject(null);
    }
  }, [handleCompareProjects, queryClient]);

  // Handle delete all projects
  const handleDeleteAllProjects = useCallback(async () => {
    try {
      setDeletingProjects(true);
      setDeleteResult(null);

      const result = await deleteAllProjects();
      setDeleteResult(result);

      if (result.success) {
        // Refresh project lists
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        alert(`Successfully deleted ${result.deletedCount} projects`);
      } else {
        alert(`Error: ${result.error || 'Failed to delete projects'}`);
      }
    } catch (error: any) {
      console.error('Failed to delete projects:', error);
      alert(`Error: ${error.message || 'Failed to delete projects'}`);
    } finally {
      setDeletingProjects(false);
    }
  }, [queryClient]);

  // Handle delete test users
  const handleDeleteTestUsers = useCallback(async () => {
    try {
      setDeletingUsers(true);
      setDeleteUsersResult(null);

      const result = await deleteTestUsers();
      setDeleteUsersResult(result);

      if (result.success) {
        // Refresh users list
        queryClient.invalidateQueries({ queryKey: ['users'] });
        alert(`Successfully deleted ${result.deletedCount} test users`);
      } else {
        alert(`Error: ${result.error || 'Failed to delete test users'}`);
      }
    } catch (error: any) {
      console.error('Failed to delete test users:', error);
      alert(`Error: ${error.message || 'Failed to delete test users'}`);
    } finally {
      setDeletingUsers(false);
    }
  }, [queryClient]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Diagnostics</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and analyze logs from PWA and Cloud Functions
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetchLogs()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            onClick={handleClearPwaLogs}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
          >
            Clear PWA Logs
          </button>
        </div>
      </div>

      {/* Database Maintenance Tools */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Database Maintenance</h2>

        <div className="space-y-4">
          {/* Fix Broken Timestamps Tool */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">Fix Broken Timestamps</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Scans all projects for broken timestamp fields and converts them to proper Firestore Timestamps.
                  This fixes the "c.toDate is not a function" error.
                </p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => handleFixTimestamps(true)}
                  disabled={fixingTimestamps}
                  className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {fixingTimestamps ? 'Scanning...' : 'Preview'}
                </button>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to fix all broken timestamps? This will modify project data.')) {
                      handleFixTimestamps(false);
                    }
                  }}
                  disabled={fixingTimestamps}
                  className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {fixingTimestamps ? 'Fixing...' : 'Fix All'}
                </button>
              </div>
            </div>

            {/* Results */}
            {timestampFixResult && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {timestampFixResult.dryRun ? 'Preview Results' : 'Fix Results'}
                </h4>
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500">Total Projects</dt>
                    <dd className="font-semibold text-gray-900">{timestampFixResult.summary.totalProjects}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Broken Projects</dt>
                    <dd className="font-semibold text-red-600">{timestampFixResult.summary.brokenProjects}</dd>
                  </div>
                  {!timestampFixResult.dryRun && (
                    <>
                      <div>
                        <dt className="text-gray-500">Fixed</dt>
                        <dd className="font-semibold text-green-600">{timestampFixResult.summary.fixedProjects}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Failed</dt>
                        <dd className="font-semibold text-red-600">{timestampFixResult.summary.failedToFix}</dd>
                      </div>
                    </>
                  )}
                </dl>

                {timestampFixResult.brokenProjectsList && timestampFixResult.brokenProjectsList.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-xs font-medium text-gray-700 mb-2">
                      {timestampFixResult.dryRun ? 'Projects That Will Be Fixed:' : 'Fixed Projects:'}
                    </h5>
                    <ul className="space-y-2 max-h-40 overflow-y-auto">
                      {timestampFixResult.brokenProjectsList.map((project: any) => (
                        <li key={project.id} className="text-xs bg-white p-2 rounded border border-gray-200">
                          <div className="font-medium text-gray-900">{project.name}</div>
                          <div className="text-gray-500">Code: {project.projectCode}</div>
                          <div className="text-red-600">Broken: {project.brokenFields.join(', ')}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {timestampFixResult.dryRun && timestampFixResult.summary.brokenProjects > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                    Click "Fix All" to repair these projects.
                  </div>
                )}

                {!timestampFixResult.dryRun && timestampFixResult.summary.fixedProjects > 0 && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    Successfully fixed {timestampFixResult.summary.fixedProjects} project(s). Refresh your project list to verify.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Delete Test Users Tool */}
          <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-orange-900">Delete Test/Seeded Users</h3>
                <p className="mt-1 text-sm text-orange-700">
                  Deletes all users with status 'invited' or 'inactive' (test/seeded users).
                  Preserves all 'active' users (real users who have logged in) and the current owner.
                </p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    if (confirm('⚠️ WARNING: This will delete all invited/inactive test users.\n\nActive users will be preserved.\n\nAre you sure?')) {
                      if (confirm('Type YES in the next prompt to confirm.')) {
                        const userConfirmation = prompt('Type YES to confirm deletion of test users:');
                        if (userConfirmation === 'YES') {
                          handleDeleteTestUsers();
                        }
                      }
                    }
                  }}
                  disabled={deletingUsers}
                  className="px-3 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {deletingUsers ? 'Deleting...' : 'Delete Test Users'}
                </button>
              </div>
            </div>

            {/* Delete Results */}
            {deleteUsersResult && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-orange-200">
                {deleteUsersResult.success ? (
                  <div className="text-sm space-y-3">
                    <div className="flex items-center gap-2 text-green-600 font-medium">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Successfully deleted {deleteUsersResult.deletedCount} test users
                    </div>

                    {deleteUsersResult.deletedUsers && deleteUsersResult.deletedUsers.length > 0 && (
                      <div>
                        <p className="font-medium text-red-600 mb-1">Deleted users:</p>
                        <ul className="list-disc list-inside text-gray-600 space-y-1">
                          {deleteUsersResult.deletedUsers.map((email: string) => (
                            <li key={email} className="text-xs">{email}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {deleteUsersResult.preservedUsers && deleteUsersResult.preservedUsers.length > 0 && (
                      <div>
                        <p className="font-medium text-green-600 mb-1">Preserved active users:</p>
                        <ul className="list-disc list-inside text-gray-600 space-y-1">
                          {deleteUsersResult.preservedUsers.map((email: string) => (
                            <li key={email} className="text-xs">{email}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm">
                    <div className="flex items-center gap-2 text-red-600 font-medium mb-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      Failed to delete test users
                    </div>
                    <p className="text-gray-600">{deleteUsersResult.error || 'Unknown error occurred'}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Delete All Projects Tool */}
          <div className="border border-red-200 rounded-lg p-4 bg-red-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-900">Delete All Test Projects</h3>
                <p className="mt-1 text-sm text-red-700">
                  <strong>WARNING:</strong> This will permanently delete ALL projects from the CRM database.
                  This action cannot be undone. Only use this to clear test data.
                </p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    if (confirm('⚠️ WARNING: This will delete ALL projects from the CRM database.\n\nThis action CANNOT be undone.\n\nAre you absolutely sure?')) {
                      if (confirm('Last chance! Type YES in the next prompt to confirm deletion.')) {
                        const userConfirmation = prompt('Type YES to confirm deletion of all projects:');
                        if (userConfirmation === 'YES') {
                          handleDeleteAllProjects();
                        }
                      }
                    }
                  }}
                  disabled={deletingProjects}
                  className="px-3 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {deletingProjects ? 'Deleting...' : 'Delete All Projects'}
                </button>
              </div>
            </div>

            {/* Delete Results */}
            {deleteResult && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-red-200">
                {deleteResult.success ? (
                  <div className="text-sm">
                    <div className="flex items-center gap-2 text-green-600 font-medium mb-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Successfully deleted {deleteResult.deletedCount} projects
                    </div>
                    <p className="text-gray-600">All project data has been removed from the database.</p>
                  </div>
                ) : (
                  <div className="text-sm">
                    <div className="flex items-center gap-2 text-red-600 font-medium mb-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      Failed to delete projects
                    </div>
                    <p className="text-gray-600">{deleteResult.error || 'Unknown error occurred'}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Portal-CRM Sync Comparison Tool */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">Portal-CRM Project Sync</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Compare Portal projects with CRM projects to identify missing or mismatched data.
                  Import Portal projects that haven't synced to the CRM.
                </p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={handleCompareProjects}
                  disabled={comparingProjects}
                  className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {comparingProjects ? 'Scanning...' : 'Compare Projects'}
                </button>
              </div>
            </div>

            {/* Comparison Results */}
            {compareResult && (
              <div className="mt-4 space-y-4">
                {/* Summary Stats */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Comparison Summary</h4>
                  <dl className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <dt className="text-gray-500">Portal Projects</dt>
                      <dd className="font-semibold text-gray-900">{compareResult.summary.totalPortalProjects}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">CRM Projects</dt>
                      <dd className="font-semibold text-gray-900">{compareResult.summary.totalCRMProjects}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Matched</dt>
                      <dd className="font-semibold text-green-600">{compareResult.summary.matchedProjects}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Portal Only</dt>
                      <dd className="font-semibold text-orange-600">{compareResult.summary.portalOnlyProjects}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">CRM Only</dt>
                      <dd className="font-semibold text-red-600">{compareResult.summary.crmOnlyProjects}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Mismatched</dt>
                      <dd className="font-semibold text-yellow-600">{compareResult.summary.mismatchedProjects}</dd>
                    </div>
                  </dl>
                </div>

                {/* Portal-Only Projects (Missing from CRM) */}
                {compareResult.portalOnlyList && compareResult.portalOnlyList.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                        {compareResult.portalOnlyList.length}
                      </span>
                      Projects in Portal but NOT in CRM
                    </h5>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {compareResult.portalOnlyList.map((project: any) => (
                        <div key={project.projectCode} className="bg-white p-3 rounded border border-orange-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{project.customerName}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                Code: <span className="font-mono">{project.projectCode}</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                Partner: {project.referralPartner} • Status: {project.status}
                              </div>
                            </div>
                            <button
                              onClick={() => handleImportProject(project.portalId)}
                              disabled={importingProject === project.portalId}
                              className="ml-3 px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              {importingProject === project.portalId ? 'Importing...' : 'Import to CRM'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CRM-Only Projects (Orphaned in CRM) */}
                {compareResult.crmOnlyList && compareResult.crmOnlyList.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        {compareResult.crmOnlyList.length}
                      </span>
                      Projects in CRM but NOT in Portal
                    </h5>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {compareResult.crmOnlyList.map((project: any) => (
                        <div key={project.projectCode} className="bg-white p-3 rounded border border-red-200">
                          <div className="font-medium text-gray-900">{project.customerName}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Code: <span className="font-mono">{project.projectCode}</span>
                          </div>
                          <div className="text-xs text-red-600 mt-1">
                            This project exists in CRM but not in Portal. It may have been deleted from Portal.
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mismatched Projects */}
                {compareResult.mismatchedList && compareResult.mismatchedList.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        {compareResult.mismatchedList.length}
                      </span>
                      Projects with Mismatched Data
                    </h5>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {compareResult.mismatchedList.map((project: any) => (
                        <div key={project.projectCode} className="bg-white p-3 rounded border border-yellow-200">
                          <div className="font-medium text-gray-900">{project.customerName}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Code: <span className="font-mono">{project.projectCode}</span>
                          </div>
                          <div className="text-xs text-yellow-700 mt-1">
                            Mismatched fields: {project.mismatch.join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Good Message */}
                {compareResult.summary.portalOnlyProjects === 0 &&
                 compareResult.summary.crmOnlyProjects === 0 &&
                 compareResult.summary.mismatchedProjects === 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    All Portal projects are properly synced to the CRM!
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-700">{severityCounts.critical}</div>
          <div className="text-sm text-red-600">Critical Issues</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-700">{severityCounts.high}</div>
          <div className="text-sm text-orange-600">High Severity</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-700">{severityCounts.medium}</div>
          <div className="text-sm text-yellow-600">Medium Severity</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-700">{severityCounts.low}</div>
          <div className="text-sm text-gray-600">Low / Info</div>
        </div>
      </div>

      {/* Source Stats */}
      {stats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-2">PWA Logs</h3>
            <div className="flex gap-4">
              <div>
                <span className="text-lg font-semibold text-gray-900">{stats.pwa.total}</span>
                <span className="text-sm text-gray-500 ml-1">total</span>
              </div>
              <div>
                <span className="text-lg font-semibold text-red-600">{stats.pwa.errors}</span>
                <span className="text-sm text-gray-500 ml-1">errors</span>
              </div>
              <div>
                <span className="text-lg font-semibold text-yellow-600">{stats.pwa.warnings}</span>
                <span className="text-sm text-gray-500 ml-1">warnings</span>
              </div>
            </div>
          </div>
          {stats.cloudFunctions && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Cloud Functions (24h)</h3>
              <div className="flex gap-4">
                <div>
                  <span className="text-lg font-semibold text-gray-900">{stats.cloudFunctions.last24Hours}</span>
                  <span className="text-sm text-gray-500 ml-1">issues</span>
                </div>
                <div>
                  <span className="text-lg font-semibold text-blue-600">{stats.cloudFunctions.lastHour}</span>
                  <span className="text-sm text-gray-500 ml-1">last hour</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Severity</label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
              className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical Only</option>
              <option value="high">High & Above</option>
              <option value="medium">Medium & Above</option>
              <option value="low">All Logs</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Source</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
              className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Sources</option>
              <option value="pwa">PWA Only</option>
              <option value="cloud-functions">Cloud Functions</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Time Range</label>
            <select
              value={hoursBack}
              onChange={(e) => setHoursBack(Number(e.target.value))}
              className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value={1}>Last Hour</option>
              <option value={6}>Last 6 Hours</option>
              <option value={24}>Last 24 Hours</option>
              <option value={72}>Last 3 Days</option>
              <option value={168}>Last Week</option>
            </select>
          </div>
          <div className="ml-auto text-sm text-gray-500">
            Showing {filteredLogs.length} of {logs.length} logs
          </div>
        </div>
      </div>

      {/* Error State */}
      {logsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load logs: {logsError instanceof Error ? logsError.message : 'Unknown error'}
        </div>
      )}

      {/* Loading State */}
      {logsLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Log List */}
      {!logsLoading && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Severity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Module
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action / Message
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No logs match the current filters
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(log.severity)}`}>
                          {log.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatLogTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          log.source === 'pwa'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {log.source === 'pwa' ? 'PWA' : 'Cloud'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {log.module}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-md">
                        {log.message || log.action}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Log Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
              <dl className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Severity</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(selectedLog.severity)}`}>
                        {selectedLog.severity.toUpperCase()}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Source</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {selectedLog.source === 'pwa' ? 'PWA (Browser)' : 'Cloud Functions'}
                    </dd>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Timestamp</dt>
                    <dd className="mt-1 text-sm text-gray-900">{new Date(selectedLog.timestamp).toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Level</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedLog.level}</dd>
                  </div>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Module</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedLog.module}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Action</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedLog.action}</dd>
                </div>
                {selectedLog.message && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Message</dt>
                    <dd className="mt-1 text-sm text-red-600 font-mono">{selectedLog.message}</dd>
                  </div>
                )}
                {selectedLog.functionName && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Function Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">{selectedLog.functionName}</dd>
                  </div>
                )}
                {selectedLog.duration !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Duration</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedLog.duration}ms</dd>
                  </div>
                )}
                {selectedLog.stack && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Stack Trace</dt>
                    <dd className="mt-1 text-xs text-gray-700 font-mono bg-gray-50 p-3 rounded overflow-x-auto whitespace-pre-wrap">
                      {selectedLog.stack}
                    </dd>
                  </div>
                )}
                {selectedLog.data && Object.keys(selectedLog.data).length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Additional Data</dt>
                    <dd className="mt-1 text-xs text-gray-700 font-mono bg-gray-50 p-3 rounded overflow-x-auto">
                      <pre>{JSON.stringify(selectedLog.data, null, 2)}</pre>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
